import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Chat as ChatIcon,
  EventAvailable as EventAvailableIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { doc, getDoc } from 'firebase/firestore';
import { initFirebase } from '../firebase';
import LoadingSpinner from '../components/common/LoadingSpinner';

const VoiceAgentPage = () => {
  const { customerId } = useParams();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [widgetCode, setWidgetCode] = useState('');
  const [isFluenteDemo, setIsFluenteDemo] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const widgetContainerRef = useRef(null);

  useEffect(() => {
    const fetchBusinessData = async () => {
      try {
        // Check if this is fluente-demo business
        const isFD = customerId === 'fluente-demo';
        setIsFluenteDemo(isFD);

        if (!isFD) {
          setLoading(false);
          return;
        }

        // Get db instance from initFirebase
        const { db } = initFirebase();
        
        // Fetch business data
        const businessRef = doc(db, 'customers', customerId);
        const businessSnap = await getDoc(businessRef);

        if (businessSnap.exists()) {
          const data = businessSnap.data();
          
          // Extract widget code from the widget field
          if (data.widget) {
            setWidgetCode(data.widget);
            console.log('Widget code loaded:', data.widget.substring(0, 100));
          } else {
            console.warn('No widget field found in document');
          }
        } else {
          console.warn('Business document does not exist');
        }
      } catch (error) {
        console.error('Error fetching business data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBusinessData();
  }, [customerId]);

  // Inject widget script into the page when widgetCode changes
  useEffect(() => {
    if (widgetCode && widgetContainerRef.current) {
      console.log('Starting widget injection...');
      console.log('Widget code:', widgetCode);
      
      // Create a temporary div to parse the HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = widgetCode;
      
      // Find all script tags
      const scripts = tempDiv.querySelectorAll('script');
      console.log('Found scripts:', scripts.length);
      
      // Append script tags to the widget container
      scripts.forEach((script, index) => {
        const newScript = document.createElement('script');
        if (script.src) {
          newScript.src = script.src;
          console.log(`Script ${index + 1} src:`, script.src);
        }
        if (script.async !== undefined) {
          newScript.async = script.async;
        }
        if (script.type) {
          newScript.type = script.type;
        }
        if (script.textContent) {
          newScript.textContent = script.textContent;
        }
        widgetContainerRef.current.appendChild(newScript);
        console.log(`Script ${index + 1} appended to widget container`);
      });

      // Find custom elements (e.g., <elevenlabs-convai>) and append to container
      const allElements = Array.from(tempDiv.children);
      console.log('All elements found:', allElements.length);
      
      allElements.forEach((element) => {
        if (element.tagName && element.tagName.includes('-')) {
          // Clone and append custom element directly to widget container
          const clonedElement = element.cloneNode(true);
          widgetContainerRef.current.appendChild(clonedElement);
          console.log('Custom element appended:', element.tagName, element);
        }
      });

      console.log('Widget injection completed');
    }
  }, [widgetCode]);

  const steps = [
    {
      label: t('voiceAgent.step1Title'),
      description: t('voiceAgent.step1Description'),
      icon: <SearchIcon />,
    },
    {
      label: t('voiceAgent.step2Title'),
      description: t('voiceAgent.step2Description'),
      icon: <PhoneIcon />,
    },
    {
      label: t('voiceAgent.step3Title'),
      description: t('voiceAgent.step3Description'),
      icon: <ChatIcon />,
    },
  ];

  const capabilities = [
    { icon: <EventAvailableIcon />, text: t('voiceAgent.capability1') },
    { icon: <SearchIcon />, text: t('voiceAgent.capability2') },
    { icon: <EditIcon />, text: t('voiceAgent.capability3') },
    { icon: <CancelIcon />, text: t('voiceAgent.capability4') },
    { icon: <PhoneIcon />, text: t('voiceAgent.capability5') },
  ];

  if (loading) {
    return <LoadingSpinner fullPage />;
  }

  if (!isFluenteDemo) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('voiceAgent.notAvailable')}
          </Alert>
          <Typography variant="h4" gutterBottom>
            {t('voiceAgent.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('voiceAgent.description')}
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 3, md: 4 }, mb: { xs: 2, sm: 3, md: 4 }, px: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3, md: 4 }, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, mb: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
          <PhoneIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: 'primary.main' }} />
          <Box>
            <Typography 
              variant="h4" 
              component="h1" 
              gutterBottom
              sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}
            >
              {t('voiceAgent.title')}
            </Typography>
            <Typography 
              variant="h6" 
              color="text.secondary"
              sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}
            >
              {t('voiceAgent.subtitle')}
            </Typography>
          </Box>
        </Box>
        <Typography 
          variant="body1" 
          paragraph
          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
        >
          {t('voiceAgent.description')}
        </Typography>
      </Paper>

      {/* How to Use - Interactive Instructions */}
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3, md: 4 }, mb: 3 }}>
        <Typography 
          variant="h5" 
          gutterBottom 
          sx={{ 
            mb: 3, 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            fontSize: { xs: '1.25rem', sm: '1.5rem' }
          }}
        >
          <VisibilityIcon color="primary" />
          {t('voiceAgent.howToUse')}
        </Typography>
        <Stepper orientation="vertical">
          {steps.map((step, index) => (
            <Step key={index} expanded active>
              <StepLabel
                StepIconComponent={() => (
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                    }}
                  >
                    {step.icon}
                  </Box>
                )}
              >
                <Typography variant="h6">{step.label}</Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body1" color="text.secondary" paragraph>
                  {step.description}
                </Typography>
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Capabilities */}
      <Paper elevation={3} sx={{ p: 4, mb: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
          {t('voiceAgent.capabilities')}
        </Typography>
        <List>
          {capabilities.map((capability, index) => (
            <Box key={index}>
              <ListItem>
                <ListItemIcon sx={{ color: 'primary.main' }}>
                  {capability.icon}
                </ListItemIcon>
                <ListItemText
                  primary={capability.text}
                  primaryTypographyProps={{ variant: 'body1', fontWeight: 500 }}
                />
              </ListItem>
              {index < capabilities.length - 1 && <Divider />}
            </Box>
          ))}
        </List>
      </Paper>

      {/* Example Requests */}
      <Paper elevation={3} sx={{ p: 4, mb: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <ChatIcon color="primary" />
          {t('voiceAgent.exampleRequests')}
        </Typography>
        <Alert severity="info" icon={<PhoneIcon />} sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight="bold">
            {t('voiceAgent.lookForButton')}
          </Typography>
        </Alert>
        <List>
          <ListItem>
            <ListItemIcon><EventAvailableIcon color="success" /></ListItemIcon>
            <ListItemText 
              primary={<Typography component="em" variant="body1">{t('voiceAgent.example1')}</Typography>}
              secondary={t('voiceAgent.example1Description')}
            />
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemIcon><SearchIcon color="info" /></ListItemIcon>
            <ListItemText 
              primary={<Typography component="em" variant="body1">{t('voiceAgent.example2')}</Typography>}
              secondary={t('voiceAgent.example2Description')}
            />
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemIcon><EditIcon color="warning" /></ListItemIcon>
            <ListItemText 
              primary={<Typography component="em" variant="body1">{t('voiceAgent.example3')}</Typography>}
              secondary={t('voiceAgent.example3Description')}
            />
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemIcon><ChatIcon color="primary" /></ListItemIcon>
            <ListItemText 
              primary={<Typography component="em" variant="body1">{t('voiceAgent.example4')}</Typography>}
              secondary={t('voiceAgent.example4Description')}
            />
          </ListItem>
        </List>
      </Paper>

      {/* Try It Now Call-to-Action */}
      {widgetCode && (
        <Paper elevation={3} sx={{ p: 4, bgcolor: 'success.50' }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
              <PhoneIcon color="success" fontSize="large" />
              {t('voiceAgent.widgetActive')}
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              {t('voiceAgent.widgetDescription')}
            </Typography>
            <Alert severity="success" sx={{ mt: 2, textAlign: 'left' }}>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                🎯 {t('voiceAgent.tryItNow')}
              </Typography>
              <Typography variant="body2">
                {t('voiceAgent.lookForButton')}
              </Typography>
            </Alert>
          </Box>
        </Paper>
      )}
      
      {/* Widget container - widget will be injected here and scoped to this page */}
      <div ref={widgetContainerRef} style={{ position: 'relative' }}></div>
    </Container>
  );
};

export default VoiceAgentPage;
